import * as React from "react";

import type { ForwardRefComponent } from "@fluentui/react-components";
import type { TabListProps } from "./TabList.types";
import { useTabListContextValues_unstable } from "@fluentui/react-components";
import { useTabListStyles } from "./useTabListStyles.styles";
import { useTablist } from "./useTabList";
import { renderTabList } from "./renderTabList";
import { customArrayCompare } from "@1js/acui-common";
import { shallowCompare } from "@fluentui/utilities";

export const TabList: ForwardRefComponent<TabListProps> = React.memo(
  React.forwardRef<HTMLElement, TabListProps>((props, ref) => {
    const state = useTablist(props, ref);
    const contextValues = useTabListContextValues_unstable(state);
    useTabListStyles(state);
    return renderTabList(state, contextValues);
  }),
  (prevProps, nextProps) => {
    const {
      topLevelTabHeaders: prevTopLevelTabHeaders,
      overflowTabHeaders: prevOverflowTabHeaders,
      ...prevRest
    } = prevProps;
    const {
      topLevelTabHeaders: nextTopLevelTabHeaders,
      overflowTabHeaders: nextOverflowTabHeaders,
      ...nextRest
    } = nextProps;
    // Perform a shallow comparison on the rest of the properties
    const shallowComparisonResult = shallowCompare(prevRest, nextRest);
    // If the shallow comparison fails, we can skip the deep comparison
    if (!shallowComparisonResult) return false;
    const selectedProps = new Set(["keytipProps"]);
    // Perform a deep comparison on the selected properties and rest by reference of the tab headers
    const overflowTabComparisonResult = customArrayCompare(selectedProps)(
      prevOverflowTabHeaders ?? [],
      nextOverflowTabHeaders ?? []
    );
    if (!overflowTabComparisonResult) return false;
    return customArrayCompare(selectedProps)(
      prevTopLevelTabHeaders,
      nextTopLevelTabHeaders
    );
  }
) as ForwardRefComponent<TabListProps>;

TabList.displayName = "next/TabList";
